<template>
  <div class="article-page">
    <article>

      <!-- Hero -->
      <div class="a-hero a-wrap">
        <p class="a-eyebrow">News</p>
        <h1>EyeJack Creator Launches WebAR Publishing for Artists, Educators and Cultural Organisations</h1>
        <p class="a-deck">Following the closure of 8th Wall, EyeJack Creator now offers a no-code WebAR platform for creating and publishing browser-based AR — no app downloads, no complex development.</p>
        <div class="a-byline">
          <span class="a-author">EyeJack</span>
          <span class="a-sep">·</span>
          <span>10 July 2026</span>
          <span class="a-sep">·</span>
          <span>3 min read</span>
        </div>
      </div>

      <!-- Hero image. Lives in landing/public/, so resolve it against the build's
           publicPath (/landing/ in prod, / in local dev) rather than hardcoding. -->
      <div class="a-wrap-wide">
        <figure class="a-full">
          <img
            class="wide"
            style="object-position: center 68%"
            :src="heroSrc"
            :srcset="heroSrcset"
            sizes="(max-width: 1200px) calc(100vw - 48px), 1104px"
            alt="A tablet held up to a geometric street mural, showing the artwork lifting off the wall in 3D — an EyeJack Creator WebAR image target experience"
            width="2500"
            height="1667"
            fetchpriority="high"
            decoding="async"
          />
          <figcaption>An AR mural seen through EyeJack Creator WebAR — the painted artwork lifts off the wall in 3D, straight in the browser.</figcaption>
        </figure>
      </div>

      <!-- Body -->
      <div class="a-body a-wrap">
        <p class="a-lead">EyeJack has launched WebAR publishing through EyeJack Creator, providing artists, educators, museums, tourism organisations and businesses with a simple 8th Wall alternative following the closure of 8th Wall.</p>

        <p>The launch expands the EyeJack Creator ecosystem, allowing users to create and publish browser-based AR experiences without requiring app downloads or complex development workflows.</p>

        <h2>A New Home for WebAR Creators</h2>
        <p>As organisations look for alternatives to 8th Wall, EyeJack is positioning itself as a creator-first WebAR platform designed for:</p>
        <ul>
          <li>Artists and designers</li>
          <li>Museums and cultural organisations</li>
          <li>Tourism operators</li>
          <li>Councils and heritage groups</li>
          <li>Educators and universities</li>
          <li>Brands and marketers</li>
        </ul>
        <p>Unlike many WebAR platforms that focus on enterprise or developer audiences, EyeJack Creator has been built around simplicity, accessibility and storytelling.</p>

        <div class="a-callout">
          <p class="a-label">The EyeJack approach</p>
          <p>Create → Publish → Learn</p>
        </div>

        <h2>AR Panels and Image Targets: Current WebAR Features</h2>
        <p>EyeJack Creator WebAR currently supports:</p>
        <ul>
          <li>Image Targets</li>
          <li>Animation Overlays</li>
          <li>Sound Integration</li>
          <li>QR Code Activation</li>
          <li>AR Panels</li>
          <li>Mobile Browser Delivery</li>
        </ul>
        <p>From AR image targets that trigger animation over a printed artwork, to AR panels that place a story in 3D space, EyeJack Creator is a no-code AR creator built for artists rather than engineers.</p>

        <h2>Built for Storytelling, Not Just Developers</h2>
        <blockquote>
          Many of our clients are museums, councils, educators and artists who need easy-to-use tools rather than developer-focused platforms.
          <cite>— Ali Lucker, Director of Business Development, EyeJack</cite>
        </blockquote>
        <p>Lucker added: “We've built EyeJack Creator and WebAR to help artists and organisations create and publish immersive experiences quickly and affordably.”</p>

        <h2>A Proven Track Record</h2>
        <p>Having delivered over 100 AR projects globally for clients including Lincoln Center, Google, Disney, Verizon, universities, museums and councils, EyeJack is now making these capabilities available through its own WebAR platform. <a href="https://eyejack.io/projects" target="_blank" rel="noopener">Learn about EyeJack AR projects</a>.</p>

        <p>For organisations looking to future-proof their WebAR experiences — including those planning an 8th Wall migration — EyeJack Creator provides a creator-friendly WebAR platform built for artists, storytelling, education, tourism and cultural engagement.</p>

        <!-- Inline CTA -->
        <div class="a-cta-inline">
          <h3>Explore EyeJack Creator WebAR</h3>
          <p>Create and publish augmented reality art to a link — no app, no code.</p>
          <a class="a-btn" href="/login">Start creating free</a>
        </div>
      </div>

    </article>

    <!-- End CTA (dark) -->
    <section class="a-cta-end">
      <div class="a-inner">
        <h2>Future-proof your WebAR</h2>
        <p>Publish augmented reality to a link that opens in any phone browser — the simple 8th Wall alternative for artists and organisations.</p>
        <a class="a-btn" href="/login">Start creating free</a>
      </div>
    </section>

  </div>
</template>

<script>
const HERO = 'eyejack-creator-webar-8th-wall-alternative'
// Pre-generated widths of the hero (the original file is the 2500w variant).
const HERO_WIDTHS = [800, 1200, 1600]

export default {
  name: 'EyejackWebarLaunch',
  data () {
    return {
      // Assets in landing/public/ are served from the build's publicPath:
      // '/landing/' in prod (and on the creator-web dev server), '/' when the
      // landing runs standalone with LANDING_PUBLIC_PATH=/.
      publicPath: process.env.BASE_URL
    }
  },
  computed: {
    // The hero is the LCP element. Serve a width-appropriate variant rather than
    // pushing the 2500w/158kB original at every device (a phone gets ~27kB).
    heroSrc () {
      return `${this.publicPath}${HERO}-1600.webp`
    },
    heroSrcset () {
      const sized = HERO_WIDTHS.map((w) => `${this.publicPath}${HERO}-${w}.webp ${w}w`)
      return [...sized, `${this.publicPath}${HERO}.webp 2500w`].join(', ')
    }
  }
}
</script>
